import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { EmptyState, ErrorState, ListSkeleton, Refreshing } from "@/components/admin/AsyncView";
import { fmtLong } from "@/components/admin/format";
import { api, errMsg } from "@/api";

const TABS = [
  { key: "akses", label: "Akses", endpoint: "/admin/access-logs", testId: "access-log-list", primary: (l) => `${l.event || "-"} - ${l.status || "-"}`, when: (l) => l.created_at },
  { key: "audit", label: "Audit", endpoint: "/admin/audit-logs", testId: "audit-log-list", primary: (l) => l.action || "-", when: (l) => l.created_at },
  { key: "notifikasi", label: "Notifikasi", endpoint: "/admin/notifications-log", testId: "notification-log-list", primary: (l) => `${l.channel || "-"} - ${l.type || "-"} - ${l.status || "-"}`, when: (l) => l.sent_at || l.created_at },
];

// Aktivitas: tiga pilihan tampilan (bukan tiga kolom sempit), baris berbasis
// waktu, filter teks yang didukung. Server membatasi 100 terbaru per jenis.
export default function ActivityPage() {
  const [params, setParams] = useSearchParams();
  const tabKey = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "akses";
  const q = params.get("q") || "";
  const tab = TABS.find((t) => t.key === tabKey);

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    const background = rows !== null;
    if (background) setRefreshing(true); else { setLoading(true); setError(null); }
    api.get(tab.endpoint).then(({ data }) => {
      if (!alive) return;
      setRows(data);
      setError(null);
    }).catch((e) => {
      if (!alive) return;
      if (!background) setError(errMsg(e));
    }).finally(() => {
      if (!alive) return;
      setLoading(false); setRefreshing(false);
    });
    return () => { alive = false; };
  }, [tab.endpoint, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = [...(rows || [])].sort((a, b) => new Date(tab.when(b) || 0) - new Date(tab.when(a) || 0));
    if (!needle) return list;
    return list.filter((l) => tab.primary(l).toLowerCase().includes(needle));
  }, [rows, q, tab]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-2xl font-semibold tracking-tight text-white">Aktivitas</h1>
        <p className="text-sm text-zinc-400">
          {rows == null ? "Memuat log…" : `${filtered.length} dari ${rows.length} entri (100 terbaru per jenis).`}
        </p>
        {refreshing ? <Refreshing /> : null}
      </div>

      <div role="tablist" aria-label="Jenis log" className="flex gap-1 overflow-x-auto border-b border-white/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tabKey === t.key}
            data-testid={`activity-tab-${t.key}`}
            onClick={() => setParams({ tab: t.key }, { replace: true })}
            className={`shrink-0 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
              tabKey === t.key ? "border-[#007AFF] text-white" : "border-transparent text-zinc-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex h-10 min-w-0 basis-full items-center gap-2 rounded-lg border border-white/10 bg-zinc-900 px-3 sm:basis-auto sm:flex-1 sm:max-w-xs">
          <span className="sr-only">Cari di log {tab.label}</span>
          <Input data-testid="activity-search" value={q}
            onChange={(e) => setParams(e.target.value ? { tab: tabKey, q: e.target.value } : { tab: tabKey }, { replace: true })}
            placeholder={`Cari di log ${tab.label.toLowerCase()}`}
            className="h-full border-0 bg-transparent p-0 text-sm placeholder:text-zinc-600 focus-visible:ring-0" />
        </label>
      </div>

      {loading && <ListSkeleton rows={6} />}
      {!loading && error && <ErrorState message={error} onRetry={() => { setRows(null); setLoading(true); setError(null); setReloadKey((k) => k + 1); }} />}
      {!loading && !error && filtered.length === 0 && (
        <EmptyState
          kind={q ? "filtered" : "zero"}
          title={q ? "Tidak ada hasil" : `Belum ada log ${tab.label.toLowerCase()}`}
          hint={q ? "Coba kata kunci lain." : "Entri baru tercatat otomatis di sini."}
        />
      )}
      {!loading && !error && filtered.length > 0 && (
        <ol data-testid={tab.testId} className="divide-y divide-white/5 overflow-hidden rounded-2xl border border-white/10 bg-zinc-900">
          {filtered.map((l) => (
            <li key={l.id || `${tab.when(l)}-${tab.primary(l)}`} className="px-4 py-3">
              <p className="break-words text-sm text-zinc-200">{tab.primary(l)}</p>
              <p className="mt-0.5 text-xs tabular-nums text-zinc-500">{fmtLong(tab.when(l))}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
